import {
  IconExclamationTriangle,
  GreyGrey20,
  GreyGrey70,
  GreyGrey80,
  GreyGrey90,
  Primary13SemiBold,
  YellowYellow50,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { createElement } from 'react'
import { NavTooltip } from 'components/NavigationV3/components/NavTooltip'
import { NAVIGATION_ANIMATION } from 'components/NavigationV3/constants/animation'

interface IntegrationsNavLinkProps {
  onClick?: () => void
  active?: boolean
  logo: React.ComponentType<{ height: string; width: string }>
  label: string
  status?: 'active' | 'warning'
  isCollapsed?: boolean
  // Copy-independent locator for tests; the label-derived testid changes when copy does.
  dataTestId?: string
}

/**
 * TODO: Reconcile this NavLink with the SubNavLink used in NavigationsV3,
 * and the link in the integrations browser modal.
 * @param props
 * @returns
 */
export const IntegrationsNavLink = (props: IntegrationsNavLinkProps) => {
  const {
    onClick,
    active,
    logo,
    label,
    status,
    isCollapsed = false,
    dataTestId
  } = props

  // Create the logo element once to avoid re-renders
  const logoElement = createElement(logo, {
    height: '16px',
    width: '16px'
  })

  const navLink = (
    <Grid
      data-testid={dataTestId}
      padding="0 8px"
      templateColumns={isCollapsed ? 'auto' : 'auto 1fr auto'}
      onClick={onClick}
      height="32px"
      gap="8px"
      borderRadius="6px"
      alignContent="center"
      alignItems="center"
      justifyContent={'flex-start'}
      cursor="pointer"
      color={active ? GreyGrey90 : GreyGrey70}
      backgroundColor={active ? GreyGrey20 : 'transparent'}
      style={{
        willChange: 'width, transform',
        transform: 'translateZ(0)',
        transition: 'background-color 0.2s ease'
      }}
      _hover={{
        backgroundColor: GreyGrey20
      }}
    >
      <GridItem
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="16px"
        height="16px"
        position="relative"
        data-testid={`navlink-logo-${label.replace(/\s+/g, '-').toLowerCase()}`}
        alignSelf="center"
        style={{
          willChange: 'transform',
          transform: 'translateZ(0)',
          transition: 'transform 0.3s ease'
        }}
      >
        {logoElement}
      </GridItem>
      {!isCollapsed && (
        <>
          <GridItem
            {...Primary13SemiBold}
            color={active ? GreyGrey90 : GreyGrey70}
            data-testid={`navlink.${label}`}
            className="nav-text"
          >
            {label}
          </GridItem>
          {status === 'warning' && (
            <GridItem className="nav-text">
              <IconExclamationTriangle
                data-testid="navlink-warning"
                style={{
                  position: 'relative',
                  top: '1px'
                }}
                color={YellowYellow50}
              />
            </GridItem>
          )}
        </>
      )}
    </Grid>
  )

  // Wrap the entire navLink in a tooltip when collapsed, consistent with other navigation components
  if (isCollapsed) {
    return (
      <NavTooltip label={label} isDisabled={!isCollapsed}>
        {navLink}
      </NavTooltip>
    )
  }

  return navLink
}
